import { ChangeDetectionStrategy, ChangeDetectorRef, Component, effect, inject, OnDestroy, OnInit, PLATFORM_ID, signal } from '@angular/core';
import { TranslatePipe } from '../../pipes/translate.pipe';
import { ActivatedRoute, NavigationEnd, NavigationStart, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { SharedService } from '../../shared/services/shared.service';
import { User } from '../../model/user.model';
import { AskQuestionModel, langAndCaddy } from '../../model/chat.model';
import { catchError, forkJoin, map, Observable, of, Subscription, switchMap, take, filter } from 'rxjs';
import { MediaModel } from '../../model/media.model';
import { UserSettingService } from '../../services/user-setting.service';
import { AuthService } from '../../auth/auth.service';
import { CourseService } from '../../services/course.service';
import { ChatService } from '../../services/chat/chat.service';
import { MediaService } from '../../services/media.service';
import { TranslationService } from '../../shared/translation.service';
import { ToasterService } from '../../shared/services/toaster.service';
import { isPlatformBrowser, Location, NgOptimizedImage } from '@angular/common';
import { TokenService } from '../../auth/token.service';
import { BackButton } from '../../model/shared.model';
import { VerifyInvitationResponse } from '../../model/course.model';
import { environment } from '../../../environments/environment';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-header',
  imports: [
    TranslatePipe,
    RouterLink,
    RouterLinkActive,
    NgOptimizedImage
  ],
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss']
})
export class HeaderComponent implements OnInit, OnDestroy {
  showMenu = signal(false);
  user = signal(new User());
  isSignedIn = signal(false);
  url = '';
  _sharedService = inject(SharedService);
  _cdr = inject(ChangeDetectorRef);
  subscriptions!: Subscription;
  _settingService = inject(UserSettingService);
  _authService = inject(AuthService);
  _courseService = inject(CourseService);
  _chatService = inject(ChatService);
  _mediaService = inject(MediaService);
  _translationService = inject(TranslationService);
  _toaster = inject(ToasterService);
  _platformId = inject(PLATFORM_ID);
  _tokenService = inject(TokenService);
  _router = inject(Router);
  _activatedRoute = inject(ActivatedRoute);
  _location = inject(Location);
  backBtn = signal(new BackButton());
  /** Count of successful in-app navigations; used to decide if `Location.back()` is safe. */
  private navigationCount = 0;
  isBrowser = signal(false);

  constructor(){
    effect(() => {
      this.backBtn.set(this._sharedService.backButton());
    });

    effect(() => {
      this.isSignedIn.set(this._sharedService.loggedIn());
    });

    this._router.events.pipe(
      filter(event => event instanceof NavigationStart || event instanceof NavigationEnd)
    ).subscribe(event => {
      if (!isPlatformBrowser(this._platformId)) {
        return;
      }

      if (event instanceof NavigationStart) {
        // A new navigation is starting. Fire any pending action-style back button
        // (e.g. a chat modal) so it can close, then reset to a generic
        // "history back" default for the target route. This runs BEFORE the
        // destination component's ngOnInit, so components that need an explicit
        // destination can override it and always win the race.
        const current = this._sharedService.backButton();
        if (current?.action) {
          this.setButtonAction(current.action);
        }

        this.showMenu.set(false);
        this.applyDefaultBackButton(event.url);
      } else if (event instanceof NavigationEnd) {
        this.url = event.url;
        this.navigationCount++;
      }
      this._cdr.markForCheck();
    });
  }

  /**
   * Sets the generic "history back" default for the given URL: hidden on the
   * home page, shown everywhere else. Feature components may override this with
   * an explicit destination/action in their own ngOnInit.
   */
  private applyDefaultBackButton(url: string): void {
    const basePath = url.split('?')[0];
    if (basePath === '/') {
      this._sharedService.backButton.set({ show: false, url: '', text: '', action: '' });
    } else {
      this._sharedService.backButton.set({ show: true, url: '', text: '', action: '' });
    }
  }

  onBackClick(): void {
    const btn = this.backBtn();

    // Action-style back button (e.g. close an open modal/panel).
    if (btn.action) {
      this.setButtonAction(btn.action);
      return;
    }

    // Explicit destination set by a feature component (e.g. tee-time detail -> /tee-times).
    if (btn.url) {
      this._router.navigateByUrl(btn.url);
      return;
    }

    // Generic case: go back to the screen the user actually came from, using the
    // real browser history. Fall back to home only when there is genuinely
    // nothing behind (e.g. the page was opened directly in a fresh tab).
    if (this.canGoBack()) {
      this._location.back();
    } else {
      this._router.navigateByUrl('/');
    }
  }

  /**
   * True when there is a previous route to return to. After an in-app navigation
   * we know one exists; after a reload `navigationCount` is reset, so we fall
   * back to the browser's history length, which survives the reload.
   */
  private canGoBack(): boolean {
    if (this.navigationCount > 1) {
      return true;
    }
    return typeof window !== 'undefined' && window.history.length > 1;
  }

  ngOnInit(){
    this.isBrowser.set(isPlatformBrowser(this._platformId));
    this.url = this._router.url;
    // Ensure the back button is correct on a fresh load/reload, even if the
    // router's initial navigation fired before this component subscribed.
    // Runs before the routed component's ngOnInit, so explicit overrides win.
    if (isPlatformBrowser(this._platformId)) {
      this.applyDefaultBackButton(this.url);
    }
    this.subscriptions = this._sharedService.user$.subscribe(user => {
      this.user.set({... user});
      if (this.user().email) {
        this.isSignedIn.set(true);
        const selectedObject: langAndCaddy = {
          caddy: this.user().caddy,
          language: { name: this.user().language?.name || '', id: this.user().language?.id || '' }
        };
        this._sharedService.selectedCaddyAndLanguage.set(selectedObject);
      }
      this._cdr.markForCheck();
    });
    if (isPlatformBrowser(this._platformId)) {
      this._chatService.getGps();
      this._activatedRoute.queryParams.subscribe(params => {
          if(params['invitationToken']) {
            console.log(params['invitationToken']);
            this._sharedService.loading.set(true);
            this.verifyCourseInvitation(params['invitationToken']);
          }
          // Non-invitation path: leave the splash up — app.component drops it
          // once the auth check resolves (signed-out) or user settings load
          // (signed-in). This prevents a "Login or Sign up" flash for users
          // who are actually signed in but whose settings haven't returned yet.
          this._cdr.markForCheck();
        });
    }
  }

  getUserSetting() {
    this._settingService.loadUserWithMedia().pipe(take(1)).subscribe({
      next: (user) => {
        this._translationService.setLanguage(user.language?.code || 'en');
        this._sharedService.loading.set(false);
        this._cdr.markForCheck();
      },
      error: (error) => {
        this._toaster.error('Error fetching user settings:', error);
        this._sharedService.loading.set(false);
        this._cdr.markForCheck();
      }
    });
  }

  goToCoursePortal() {
    window.open(environment.coursePortalUrl, '_blank');
  }

  setButtonAction(action: string){
    this._sharedService.backButtonAction.set({ type: 'action', action });
  }

  loginWithSso() {
    this.showMenu.set(false);
    this._authService.redirectToSso();
  }

  openBookingForm() {
    this.showMenu.set(false);
    this._sharedService.openTeeTimeBookingForm();
  }


  logOut() {
    this.showMenu.set(false);
    this._tokenService.logout();
  }

  setQuestion(question: string){
    this.showMenu.set(false);
    let questionModel = new AskQuestionModel();
    questionModel.textQuestion = question;
    questionModel.showProcessingText = true;
    this._sharedService.setQuestion(questionModel);
  }

  verifyCourseInvitation(invitationToken: string){
    this._courseService.verifyCourseInvitation(invitationToken).subscribe({
      next: (res: VerifyInvitationResponse) => {
        if(res.data.action === 'login') {
          this._router.navigate(['/login'], {
            queryParams: {
              email: res.data.email,
              source: 'course'
            }
          });
          this._sharedService.setLocalStorage('inviteSessionToken', res.data.inviteSessionToken);
          this._sharedService.setLocalStorage('invitedBusinessName', res.data.businessName);
          this._sharedService.loading.set(false);
        } else if(res.data.action === 'register') {
          this._sharedService.setLocalStorage('inviteSessionToken', res.data.inviteSessionToken);
          this._sharedService.setLocalStorage('invitedBusinessName', res.data.businessName);
          if (this._tokenService.isSignedIn()) {
            this._tokenService.logout('/signup?email=' + res.data.email + '&source=course');
          } else {
            this._router.navigate(['/signup'], { queryParams: { email: res.data.email, source: 'course' } });
          }
        } else if(res.data.action === 'claim') {
          this._sharedService.setLocalStorage('inviteSessionToken', res.data.inviteSessionToken);
          this._sharedService.setLocalStorage('invitedBusinessName', res.data.businessName);
          // Trigger LayoutComponent to open the claim modal via query param
          this._router.navigate(['/'], {
            queryParams: {
              source: 'course-invitation'
            }
          });
          this._sharedService.loading.set(false);
        }

        console.log(res);
        this._cdr.markForCheck();
      },
      error: (error: unknown) => {
        this._sharedService.loading.set(false);
        this._toaster.error('Error verifying course invitation:', String(error));
        this._cdr.markForCheck();
      }
    });
  }

  ngOnDestroy(){
    this.subscriptions.unsubscribe();
  }
}
